import {useEffect, useState} from 'react';
import {machinesApi, transactionsApi, productsApi} from '../../../lib/apis';

export type Machine = {
    id: string;
    name: string;
    location: string;
    status: 'online' | 'offline';
    lastSyncAt: string | null;
    isActive: boolean;
};

export const useDashboardPresenter = () => {
    const [machines, setMachines] = useState<Machine[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [summaries, setSummaries] = useState({
        day: {transactions: 0, revenue: 0},
        week: {transactions: 0, revenue: 0},
        month: {transactions: 0, revenue: 0},
    });

    const [productCounts, setProductCounts] = useState<{ active: number; inactive: number }>({active: 0, inactive: 0});

    useEffect(() => {
        const loadMachines = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await machinesApi.get<{ data: any[] }>("/machines");
                const mapped: Machine[] = ((res as any)?.data?.data || (res as any)?.data || (res as any) || []).map((m: any) => ({
                    id: m.id,
                    name: m.name,
                    location: m.location,
                    status: m.status,
                    lastSyncAt: m.lastSyncAt ? String(m.lastSyncAt) : null
                }));
                setMachines(mapped);
            } catch (e: any) {
                setError(e.message || 'Failed to load');
            } finally {
                setLoading(false);
            }
        };

        const loadProducts = async () => {
            try {
                const res = await productsApi.get<{ data: any[] }>("/products");
                const list = ((res as any)?.data?.data || (res as any)?.data || []) as any[];
                let active = 0, inactive = 0;
                for (const p of list) {
                    const isActive = typeof p.active === 'boolean' ? !!p.active : true;
                    if (isActive) active++; else inactive++;
                }
                setProductCounts({active, inactive});
            } catch (e) {
                // ignore silently on dashboard
            }
        };

        const computeSummaries = (transactions: any[]) => {
            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const startOfWeek = new Date(startOfDay); // assuming week starts on Monday
            const dayOfWeek = (startOfDay.getDay() + 6) % 7; // 0 = Monday
            startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

            const summary = {
                day: {transactions: 0, revenue: 0},
                week: {transactions: 0, revenue: 0},
                month: {transactions: 0, revenue: 0},
            };

            for (const t of transactions) {
                const createdAt = t.createdAt ? new Date(t.createdAt) : null;
                if (!createdAt) continue;
                const total = (t.qty || 0) * (t.unitPrice || 0);
                if (createdAt >= startOfDay) {
                    summary.day.transactions += 1;
                    summary.day.revenue += total;
                }
                if (createdAt >= startOfWeek) {
                    summary.week.transactions += 1;
                    summary.week.revenue += total;
                }
                if (createdAt >= startOfMonth) {
                    summary.month.transactions += 1;
                    summary.month.revenue += total;
                }
            }

            setSummaries(summary);
        };

        let timer: any;
        const pollTransactions = async () => {
            try {
                const res = await transactionsApi.get<{ data: any[] }>("/transactions");
                const list = ((res as any)?.data?.data || (res as any)?.data || []) as any[];
                computeSummaries(list);
            } catch (e: any) {
                // don't crash dashboard; optionally set error
            }
        };

        loadMachines();
        loadProducts();
        pollTransactions();
        timer = setInterval(pollTransactions, 5000);

        return () => {
            if (timer) clearInterval(timer);
        };
    }, []);

    return {state: {machines, loading, error, summaries, productCounts}};
};
